/* Small, opt-in diagnostic layer. Shopify owns all commerce events. */
(() => {
  if (window.phqInteractions) return;
  const source = document.getElementById('phq-interaction-context');
  if (!source) return;
  let context;
  try { context = JSON.parse(source.textContent); } catch { return; }
  const kinds = new Set(['section_view', 'info_open', 'media_interaction', 'help_click']);
  const fields = ['section_id', 'control_id', 'action', 'media_id', 'media_type', 'destination_path', 'destination_host'];
  const visibleSections = new Set();
  const observed = new WeakSet();

  function allowed() {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      // Unknown consent/API state must never queue an interaction for later.
      return privacy?.analyticsProcessingAllowed() === true;
    } catch { return false; }
  }

  function visible(element) {
    if (document.visibilityState !== 'visible' || !element?.isConnected) return false;
    for (let node = element; node instanceof Element; node = node.parentElement) {
      if (node.hidden || (node.tagName === 'DETAILS' && !node.open)) return false;
      const style = getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
    }
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 &&
      rect.top < window.innerHeight && rect.left < window.innerWidth;
  }

  function emit(kind, element, detail = {}) {
    if (!kinds.has(kind) || !allowed()) return;
    const scope = element?.closest('[data-track-product-id]');
    const variantId = scope ? scope.dataset.trackVariantId : document.querySelector('.phq-product-buy [name="id"]')?.value;
    const variant = !scope && context.variants.find(item => item.id === variantId);
    const payload = {
      page_type: context.page_type,
      origin_page_path: context.origin_page_path,
      page_location: location.origin + context.origin_page_path,
      page_version: context.page_version,
      event_time_ms: Date.now(),
      test_traffic: context.test_traffic || new URLSearchParams(location.search).get('phq_debug') === '1' ? 'test' : 'production',
    };
    const productId = scope?.dataset.trackProductId || context.product_id;
    if (productId) {
      payload.product_id = productId;
      if (variantId) payload.variant_id = variantId;
      if (scope?.dataset.trackSku || variant?.sku) payload.sku = scope?.dataset.trackSku || variant.sku;
    }
    fields.forEach(key => {
      if (typeof detail[key] === 'string') payload[key] = detail[key].slice(0, 100);
    });
    const event = `${context.page_type === 'product' ? 'product' : 'content'}_${kind}`;
    // Shopify delivers customData to subscribed pixels. Publication is not a GA4 receipt.
    // No queue/retry or direct Google fallback: consent and commerce remain separate.
    if (typeof window.Shopify?.analytics?.publish === 'function') {
      try {
        const result = window.Shopify.analytics.publish(`plumbhq:${event}`, payload);
        // Handle both synchronous APIs and rejected promises without affecting the UI.
        if (result && typeof result.catch === 'function') result.catch(() => {});
      } catch { /* Diagnostics must not interrupt storefront behaviour. */ }
    }
    if (typeof window.clarity === 'function') {
      const marker = [event, context.origin_page_path, detail.section_id || detail.control_id || detail.action]
        .filter(Boolean).join(':');
      try { window.clarity('event', marker); } catch { /* Optional destination. */ }
    }
  }

  function checkView(element) {
    const id = element.dataset.trackSection;
    if (!id) return;
    if (!visible(element)) {
      visibleSections.delete(id);
      return;
    }
    if (visibleSections.has(id)) return;
    // Remember the current exposure even without consent; never replay it on acceptance.
    visibleSections.add(id);
    emit('section_view', element, { section_id: id });
  }

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) checkView(entry.target);
      else visibleSections.delete(entry.target.dataset.trackSection);
    })) : null;

  function scan(root = document) {
    const elements = [...root.querySelectorAll('[data-track-section]')];
    if (root.matches?.('[data-track-section]')) elements.push(root);
    elements.forEach(element => {
      if (!observed.has(element)) {
        observed.add(element);
        observer?.observe(element);
      }
      checkView(element);
    });
  }

  document.addEventListener('click', event => {
    const summary = event.target.closest?.('summary');
    const disclosure = summary?.parentElement;
    if (disclosure?.matches('details[data-track-open]') && !disclosure.open && allowed()) {
      // Native details activation completes after the click's microtasks.
      setTimeout(() => {
        if (!event.defaultPrevented && disclosure.open && !disclosure.hidden) {
          emit('info_open', disclosure, { section_id: disclosure.dataset.trackOpen });
        }
      }, 0);
    }
    const control = event.target.closest?.('[data-track-help]');
    if (!control) return;
    const detail = { control_id: control.dataset.trackHelp };
    // Chat, email and telephone actions deliberately have no destination value.
    if (!control.classList.contains('chat-now-btn')) {
      try {
        const url = new URL(control.getAttribute('href'), location.origin);
        if (['http:', 'https:'].includes(url.protocol)) {
          detail.destination_host = url.hostname;
          detail.destination_path = url.pathname;
        }
      } catch { /* Non-link control. */ }
    }
    emit('help_click', control, detail);
  });

  ['play', 'ended'].forEach(type => document.addEventListener(type, event => {
    if (!event.target.matches?.('video[data-track-media]')) return;
    emit('media_interaction', event.target, {
      media_id: event.target.dataset.trackMedia, media_type: 'video',
      action: type === 'play' ? 'video_start' : 'video_complete',
    });
  }, true));

  const safely = callback => (...args) => {
    try { return callback(...args); } catch { /* Fail closed without affecting the caller. */ }
  };
  window.phqInteractions = Object.freeze({ emit: safely(emit), scan: safely(scan) });
  scan();
  // Only opt-in sections are observed; no click scraping or global text matching.
  new MutationObserver(records => records.forEach(record => {
    record.addedNodes.forEach(node => { if (node instanceof Element) scan(node); });
    record.removedNodes.forEach(node => {
      if (!(node instanceof Element)) return;
      [node, ...node.querySelectorAll('[data-track-section]')].forEach(element => {
        observer?.unobserve(element);
        observed.delete(element);
        if (!element.isConnected) visibleSections.delete(element.dataset.trackSection);
      });
    });
  })).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('visibilitychange', () => scan());
  document.addEventListener('toggle', event => scan(event.target), true);
  window.addEventListener('pageshow', () => scan());
})();
